import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { personService } from '@shared/services/personService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

export const useFileForm = (person) => {
    const { t } = useTranslation();
    const [fileData, setFileData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingFile, setEditingFile] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileTypes, setFileTypes] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const fileValidationSchema = yup.object({
        personsFileTypeId: yup.mixed()
            .when('$isEditing', {
                is: true,
                then: () => yup.mixed().notRequired(),
                otherwise: () => yup.mixed()
                    .required(t('validation.person.file.typeRequired'))
                    .transform((value) => (value === '' ? undefined : Number(value)))
            }),
        description: yup.string()
            .max(255, t('validation.person.file.descriptionLength')),
        active: yup.boolean().default(true)
    });

    const {
        register: fileFormRegister,
        formState: { errors: fileErrors },
        setValue: setFileValue,
        watch,
        handleSubmit,
        reset
    } = useForm({
        resolver: yupResolver(fileValidationSchema),
        defaultValues: {
            personsFileTypeId: '',
            description: '',
            active: true
        },
        context: { isEditing }
    });

    const isActiveChecked = watch('active');

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const fetchFiles = async () => {
        try {
            if (person?.id) {
                const data = await personService.getPersonFiles(person.id);
                setFileData(data || []);
            }
        } catch (error) {
            console.error('Error fetching files:', error);
            notifyError(t('persons.file.fetchError'));
        }
    };

    const fetchFileTypes = async () => {
        try {
            const types = await personService.getFileTypes();
            setFileTypes(types);
        } catch (error) {
            console.error('Error fetching file types:', error);
        }
    };

    useEffect(() => {
        fetchFiles();
        fetchFileTypes();
    }, [person]);

    const handleEdit = (file) => {
        setIsEditing(true);
        setEditingFile(file);
        setFileValue('personsFileTypeId', file.personsFileType?.id || '');
        setFileValue('description', file.description || '');
        setFileValue('active', Boolean(file.isActive));
    };

    const submitFileForm = async (formData) => {
        setIsSubmitted(true);
        try {
            if (isEditing && editingFile) {
                await personService.updatePersonFile(
                    person.id,
                    editingFile.id,
                    {
                        personId: person.id,
                        description: formData.description,
                        active: formData.active === undefined ? false : formData.active
                    }
                );
                notifySuccess(t('persons.file.updateSuccess'));
            } else {
                const fileData = {
                    file: selectedFile,
                    active: formData.active === undefined ? false : formData.active,
                    personsFileTypeId: parseInt(formData.personsFileTypeId, 10),
                    description: formData.description || ''
                };

                await personService.addPersonFile(person.id, fileData);
                notifySuccess(t('persons.file.addSuccess'));
            }

            setIsSubmitted(false);
            reset({
                personsFileTypeId: '',
                description: '',
                active: true
            });
            setSelectedFile(null);
            setIsEditing(false);
            setEditingFile(null);
            await fetchFiles();
            return true;
        } catch (error) {
            console.error('File operation failed:', error);
            if (error.response) {
                console.error('Server response:', error.response.data);
            }
            return false;
        }
    };

    const handleDelete = async (fileId) => {
        try {
            await personService.deletePersonFile(fileId);
            notifySuccess(t('persons.file.deleteSuccess'));
            await fetchFiles();
        } catch (error) {
            notifyError(t('persons.file.deleteError'));
        }
    };

    const handleCancel = () => {
        setIsSubmitted(false);
        reset({
            personsFileTypeId: '',
            description: '',
            active: true
        });
        setSelectedFile(null);
        setIsEditing(false);
        setEditingFile(null);
    };

    return {
        fileData,
        fileTypes,
        fileFormRegister,
        fileErrors,
        setFileValue,
        handleEdit,
        handleDelete,
        submitFileForm,
        isEditing,
        handleSubmit,
        handleCancel,
        isActiveChecked,
        handleFileChange,
        selectedFile,
        isSubmitted
    };
}; 